import exifr from "exifr";

import getFirebase from "../../../api/firebase";
import type { Media, UploadMediaParams } from "./types";

const getPhotoDate = async (file: File): Promise<Date> => {
  try {
    const exif = await exifr.parse(file, ["DateTimeOriginal"]);
    if (exif?.DateTimeOriginal instanceof Date) {
      return exif.DateTimeOriginal;
    }
  } catch {
    // EXIF parsing failed, fall back to file date
  }
  return new Date(file.lastModified);
};

/**
 * Upload media with progress callback support
 * Uses Firebase Storage's uploadTask.on() for real-time progress updates
 */
export const uploadMediaWithProgress = async (
  { id, file, productId, userId }: UploadMediaParams,
  onProgress?: (progress: number) => void,
): Promise<Media> => {
  const [firebase, photoDate] = await Promise.all([getFirebase(), getPhotoDate(file)]);

  // Get file extension
  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";

  // Upload to Storage
  const mediaRef = firebase.storage().ref().child(`users/${userId}/${id}.${extension}`);

  return new Promise((resolve, reject) => {
    const uploadTask = mediaRef.put(file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(progress);
      },
      (error) => reject(error),
      async () => {
        try {
          const downloadUrl = await mediaRef.getDownloadURL();

          // Save metadata to Firestore
          const mediaObject: Media = {
            uid: id,
            filename: `${id}.${extension}`,
            url: downloadUrl,
            time: firebase.firestore.Timestamp.fromDate(photoDate),
            userId,
            productId,
          };

          await firebase.firestore().collection("media").doc(id).set(mediaObject);

          resolve(mediaObject);
        } catch (error) {
          reject(error);
        }
      },
    );
  });
};

/**
 * Delete a single media file from Firebase Storage
 */
export const deleteMediaFileFromStorage = async (
  userId: string,
  filename: string,
): Promise<void> => {
  const firebase = await getFirebase();

  const storageRef = firebase.storage().ref().child(`users/${userId}/${filename}`);

  try {
    await storageRef.delete();
  } catch (error: any) {
    // Ignore "object-not-found" errors - file may have been deleted already
    if (error.code !== "storage/object-not-found") {
      throw error;
    }
  }
};

/**
 * Delete a media document from Firestore
 */
export const deleteMediaDocument = async (mediaId: string): Promise<void> => {
  const firebase = await getFirebase();

  await firebase.firestore().collection("media").doc(mediaId).delete();
};

/**
 * Delete all media associated with a product
 * - Deletes files from Firebase Storage
 * - Deletes metadata documents from Firestore
 */
export const deleteAllProductMedia = async (
  productId: string,
  userId?: string,
): Promise<{ deletedCount: number; errors: string[] }> => {
  const firebase = await getFirebase();

  // Build query with optional userId filter (required by Firestore security rules for non-admin users)
  let query = firebase.firestore().collection("media").where("productId", "==", productId);

  if (userId) {
    query = query.where("userId", "==", userId);
  }

  const results = await query.get();

  const errors: string[] = [];
  let deletedCount = 0;

  // Delete each media item (storage file + Firestore document)
  for (const doc of results.docs) {
    const media = doc.data() as Media;

    try {
      // Delete from Storage
      await deleteMediaFileFromStorage(media.userId, media.filename);

      // Delete Firestore document
      await deleteMediaDocument(media.uid);

      deletedCount++;
    } catch (error: any) {
      errors.push(`Failed to delete ${media.filename}: ${error.message}`);
    }
  }

  return { deletedCount, errors };
};
